import type {
  FormState,
  FormStateValue,
} from '@de/providers/DataSourceManager.types';
import { formStateValuesEqual } from '@de/utils/form-state';

/**
 * Copy of one form-state entry.
 *
 * A list is copied so a host that mutates the payload cannot change the
 * Data Source Manager snapshot it was projected from.
 */
const copyFormStateValue = (value: FormStateValue): FormStateValue =>
  Array.isArray(value) ? [...value] : value;

/**
 * The form-state keys a host asked to hear, in the order it declared them.
 *
 * Keys the snapshot does not have are left out. Keys the host did not
 * declare are left out, including ones that arrived on `initialFormState`.
 * The declared list is the only allowlist — it is never inferred.
 */
export const selectObservedFormState = (
  snapshot: FormState,
  observedKeys: readonly string[],
): FormState => {
  const selected: Record<string, FormStateValue> = {};
  for (const key of observedKeys) {
    if (!Object.prototype.hasOwnProperty.call(snapshot, key)) continue;
    selected[key] = copyFormStateValue(snapshot[key]);
  }
  return selected;
};

/** Value equality for two observed projections. Key order follows the declaration. */
export const observedFormStatesEqual = (
  left: FormState,
  right: FormState,
): boolean => {
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(right, key) &&
      formStateValuesEqual(left[key], right[key]),
  );
};
